import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';

// Store
import { Dispatch, RootState } from '../../../store/store';

// Constants
import { CYCLE_MEASUREMENT_SYMBOL, PAGE_TITLE, TEXT_LENGTH_LIMITS } from '../../../constants/common';
import { Action } from '../../../enums/action';

// Components
import InteractiveButton from '../../../components/InteractiveButton/InteractiveButton';
import InputControl from '../../../components/InputControl/InputControl';
import SelectGroupOne from '../../../components/Forms/SelectGroup/SelectGroupOne';
import SwitcherOne from '../../../components/Switchers/SwitcherOne';
import { useAbilityContext } from '../../../components/Can/Can';
import ApplySetupToControllerModal from '../../../components/ApplyControllerSetupModal/ApplySetupToControllerModal';
import { ButtonType } from '../../../types/button-type';

type FormValues = {
  serial_number: string | null;
  name: string | null;
  is_active: boolean | null;
  locationId: string | null;
};

type EditControllerProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

const EditControllerForm: React.FC<EditControllerProps> = ({
  controller,
  updateController,
  getLocations,
  resetLocations,
}) => {

  const { id } = useParams();
  const ability = useAbilityContext();

  const [isLocationLoaded, setIsLocationLoaded] = useState(false);
  const [options, setOptions] = useState(null);
  const [showApplySetupModal, setShowApplySetupModal] = useState(false);

  const canApplySetup = ability?.can(Action.Update, PAGE_TITLE.CONTROLLER_SETUPS);

  const formik = useFormik<FormValues>({
    initialValues: {
      serial_number: null,
      name: null,
      is_active: null,
      locationId: null,
    },
    validationSchema: Yup.object({
      serial_number: Yup.string()
        .required('Required')
        .min(TEXT_LENGTH_LIMITS.MIN, `Min first name length is ${TEXT_LENGTH_LIMITS.MIN} characters`)
        .max(TEXT_LENGTH_LIMITS.MAX, `Max first name length is ${TEXT_LENGTH_LIMITS.MAX} characters`),
      name: Yup.string()
        .required('Required')
        .min(TEXT_LENGTH_LIMITS.MIN, `Min last name length is ${TEXT_LENGTH_LIMITS.MIN} characters`)
        .max(TEXT_LENGTH_LIMITS.MAX, `Max last name length is ${TEXT_LENGTH_LIMITS.MAX} characters`),
      is_active: Yup.boolean().nullable(),
      locationId: Yup.string().nullable(),
    }),
    onSubmit: async (values) => {
      const {
        serial_number,
        name,
        is_active,
        locationId,
      } = values;
      await updateController({
        id, formData: {
          serial_number,
          name,
          is_active,
          locationId,
        }
      });
    },
  });

  const isDisabledBtn = Boolean((formik.touched.name && formik.errors.name) || (formik.touched.serial_number && formik.errors.serial_number));

  const handleSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;

    formik.handleChange({
      target: { name, value },
    });
  };

  const populateFormWithController = (controller: Record<string, any>) => {
    Object.entries(controller).forEach(([key, value]) => {
      formik.handleChange({
        target: {
          name: key,
          value: value,
        },
      });
    });
  };

  const prepareLocationOptions = async () => {
    const locations = await getLocations();

    const optionsByLocation = locations?.map((location) => ({
      value: location.id,
      label: location.name,
      disabled: false,
    })) || [];

    const options = [
      { value: '', label: 'Client not selected', disabled: true },
      ...optionsByLocation,
    ];

    setOptions(options);
    setIsLocationLoaded(true);
  };

  useEffect(() => {
    if (controller?.id) {
      populateFormWithController(controller);
      prepareLocationOptions();
    }

    return () => {
      resetLocations();
    };
  }, [id, controller]);

  return (
    <>
      {!isLocationLoaded ? (
        <div className="flex items-center justify-center">Loading...</div>
      ) : (
        <div className="p-edit-controller-form pb-4 md:pb-25">
          <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stroke px-7 py-4 dark:border-strokedark">
              <h3 className="font-medium text-black dark:text-white">
                Edit Controller {!!controller?.units && `(${CYCLE_MEASUREMENT_SYMBOL[controller.units]})`}
              </h3>
              {canApplySetup && (
                <InteractiveButton
                  text="Apply controller backup"
                  type={ButtonType.PrimaryInverse}
                  onClick={() => setShowApplySetupModal(true)}
                />
              )}
            </div>
            {id && (
              <ApplySetupToControllerModal
                isVisible={showApplySetupModal}
                controllerId={id}
                onClose={() => setShowApplySetupModal(false)}
              />
            )}
            <form className="flex flex-col gap-5.5 p-7" onSubmit={formik.handleSubmit}>
              <div className="w-full">
                <InputControl
                  label="Serial Number (not edited)"
                  type="text"
                  value={formik.values.serial_number}
                  disabled
                />
              </div>
              <div className="w-full">
                <InputControl
                  label="Name"
                  type="text"
                  name="name"
                  placeholder="Enter your controller name"
                  value={formik.values.name}
                  customOnChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  showError={formik.touched.name && formik.errors.name}
                  errorText={formik.errors.name}
                />
              </div>
              <div className="w-full">
                <SelectGroupOne
                  label="Client"
                  handleChange={handleSelect}
                  options={options}
                  selectedOption={formik.values.locationId ?? ''}
                  name="locationId"
                />
              </div>
              <SwitcherOne
                name="is_active"
                enabled={formik.values.is_active}
                handleChange={formik.handleChange}
                label={formik.values.is_active ? 'Activated' : 'Deactivated'}
              />
              <div className="flex justify-end gap-4.5">
                <InteractiveButton
                  text="Update"
                  submit
                  disabled={isDisabledBtn}
                  isLoading={formik.isSubmitting}
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  controller: state.controllers.controller,
});

const mapDispatch = (dispatch: Dispatch) => ({
  updateController: dispatch.controllers.updateController,
  resetControllerState: dispatch.controllers.resetState,
  getLocations: dispatch.locations.getLocations,
  resetLocations: dispatch.locations.resetList,
});

export default connect(mapState, mapDispatch)(EditControllerForm);
